import type { IndexeddbPersistence } from 'y-indexeddb';
import type * as Y from 'yjs';
import type { PersistenceAdapter } from './persistence-adapter';

/**
 * Persistence adapter that performs no local persistence. Used in share mode:
 * share viewers have ephemeral read-through access to server-owned docs and
 * must not write anything to IndexedDB. Design decision #9 — share mode is
 * selected via dependency injection, not runtime flags.
 *
 * `bindDocAndSync`, `unbindDoc`, and `deleteYjsState` are no-ops. `bindDoc`
 * throws because the registry never calls it directly (only via
 * `bindDocAndSync`); if it's ever reached, that's a contract violation.
 */
export class NoOpPersistence implements PersistenceAdapter {
  bindDoc(_entityId: string, _doc: Y.Doc): IndexeddbPersistence {
    throw new Error('[NoOpPersistence] bindDoc is not supported in share mode');
  }

  async bindDocAndSync(_entityId: string, _doc: Y.Doc): Promise<void> {
    // No-op: share docs hydrate exclusively from the server via sync-step2.
  }

  async unbindDoc(_entityId: string): Promise<void> {
    // No-op: nothing was ever opened, so nothing needs closing.
  }

  async deleteYjsState(_entityId: string): Promise<void> {
    // No-op: share viewers can't delete persistent state they never wrote.
  }

  async overwriteYjsState(_entityId: string, _doc: Y.Doc): Promise<void> {
    // No-op: share mode has no local persistence to overwrite.
  }
}
